import { Box, Paper, Typography } from "@mui/material";
import { PaletteColor } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Stage } from "../../../shared/CaseConfiguration";
import { OfferStatus } from "../../../shared/CaseModel";
import { percentage } from "../../../shared/percentage";
import { gainFill, TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SectionLabel } from "../../general/SectionLabel";
import { TonalBadge } from "../../general/TonalBadge";
import { offerLook, statusKey } from "./offerStatus";
import { Named } from "./SettlementOfferCard";
import { Offer } from "./SettlementOfferDialog";
import {
  claimProgress,
  decisiveOffer,
  isSettled,
  offerCounts,
  OpponentSettlements,
  settledClaims,
} from "./settlementModel";

/** The height of the bar of a claimed amount, in px. */
const BAR_HEIGHT = 10;

const bold = (
  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }} />
);

/**
 * The offer that says where a party stands with an opponent: once the two
 * have settled the accepted one, before that an offer that is pending rather
 * than whatever came before it.
 */
function leadingOffer(pair: OpponentSettlements): Offer | undefined {
  if (isSettled(pair)) return decisiveOffer(pair.offers);
  const pending = pair.offers.filter(
    ({ status }) => status === OfferStatus.Offered,
  );
  return pending[pending.length - 1] ?? decisiveOffer(pair.offers);
}

/**
 * Where a party stands with an opponent, as its leading offer has it: in a
 * word with its tone, and in a sentence.
 */
export function useStanding(): (pair: OpponentSettlements) => {
  badge: string;
  tone?: PaletteColor;
  detail: string;
} {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement",
  });

  return (pair) => {
    const offer = leadingOffer(pair);
    if (!offer)
      return { badge: t("standing.none"), detail: t("standing.noOffers") };
    const values = {
      amount: currency.format(offer.payment.amount),
      stage: t(Stage[offer.stage], { keyPrefix: "case.stages" }),
    };
    switch (offer.status) {
      case OfferStatus.Accepted:
        return {
          badge: isSettled(pair) ? t("standing.settled") : t("standing.partly"),
          tone: theme.palette.data.positive,
          detail: t("standing.agreed", values),
        };
      case OfferStatus.Offered:
        return {
          badge: t("standing.pending"),
          tone: theme.palette.primary,
          detail: t("standing.offered", values),
        };
      case OfferStatus.Rejected:
        return {
          badge: t("standing.rejected"),
          tone: theme.palette.data.negative,
          detail: t("standing.refused", values),
        };
      case OfferStatus.Draft:
        return {
          badge: t("standing.draft"),
          detail: t("standing.drafted", values),
        };
    }
  };
}

/**
 * Where a party stands with its settlements: how many offers there are, where
 * it stands with each opponent, and how far each of its claims is settled.
 */
const SettlementSummary = ({
  partyName,
  pairs,
}: {
  partyName: string;
  pairs: OpponentSettlements[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement",
  });
  const statusName = (status: OfferStatus) =>
    t(`offerDialog.status.${statusKey(status)}`);
  const standingOf = useStanding();

  const counts = offerCounts(pairs);
  const claims = claimProgress(pairs);
  const part = { px: 2.5, pt: 2, pb: 2.25 };
  const heading = { fontWeight: 700 };
  const swatch = (color: string, label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: color }} />
      {label}
    </Box>
  );
  const offeredFill = theme.palette.primary.light;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={part}>
        <SectionLabel component="h2">{t("summary")}</SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("description", { name: partyName })}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            columnGap: 3.25,
            rowGap: 2,
            mt: 1.75,
          }}
        >
          {(
            ["total", "pending", "accepted", "rejected", "drafts"] as const
          ).map((key) => (
            <div key={key}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                {t(`counts.${key}`)}
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
              >
                {counts[key]}
              </Typography>
            </div>
          ))}
        </Box>
      </Box>

      {/* where the party stands with each opponent */}
      <Box sx={{ ...part, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="subtitle2" component="h3" sx={heading}>
          {t("standing.title")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 1.5,
            mt: 1.25,
          }}
        >
          {pairs.map((pair) => {
            const { badge, tone, detail } = standingOf(pair);
            return (
              <Paper
                key={pair.opponent.id}
                variant="outlined"
                sx={{ borderRadius: 2.5, px: 1.75, py: 1.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {pair.opponent.name}
                  </Typography>
                  <TonalBadge tone={tone}>{badge}</TonalBadge>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.625, color: "text.secondary" }}
                >
                  <Trans
                    i18nKey="case.analytics.settlement.standing.claims"
                    count={pair.claims.length}
                    values={{ settled: settledClaims(pair) }}
                    components={{ bold }}
                  />
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {detail}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* how far each claim is settled */}
      <Box sx={{ ...part, borderTop: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            columnGap: 2.5,
            rowGap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle2" component="h3" sx={heading}>
              {t("progress.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.375, color: "text.secondary" }}
            >
              {t("progress.description")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.75,
              typography: "caption",
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            {swatch(gainFill(theme), t("progress.settled"))}
            {swatch(offeredFill, t("progress.offered"))}
            {swatch(theme.palette.action.selected, t("progress.open"))}
          </Box>
        </Box>

        {claims.map((claim) => {
          const settledWith = claim.opponents.filter(
            ({ status }) => status === OfferStatus.Accepted,
          ).length;
          return (
            <Paper
              key={claim.id}
              variant="outlined"
              sx={{ mt: 1.5, borderRadius: 2.5, overflow: "hidden" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  columnGap: 1.75,
                  rowGap: 1,
                  px: 1.75,
                  py: 1.25,
                  bgcolor: "background.default",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 1.25,
                    rowGap: 1,
                  }}
                >
                  <Typography variant="body2">
                    <Named
                      label={t("number", {
                        keyPrefix: "case.claims",
                        ordinal: claim.number,
                      })}
                      title={claim.title}
                    />
                  </Typography>
                  {claim.opponents.map(({ opponent, status }) => {
                    const look =
                      status === undefined
                        ? undefined
                        : offerLook(theme, status);
                    return (
                      <Box
                        key={opponent.id}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          pl: 1,
                          pr: 1.125,
                          py: 0.25,
                          borderRadius: 999,
                          border: 1,
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          typography: "caption",
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: look?.color ?? "text.disabled",
                          }}
                        />
                        {opponent.name}
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: look?.ink ?? "text.secondary",
                          }}
                        >
                          {status === undefined
                            ? t("standing.none")
                            : status === OfferStatus.Accepted
                              ? t("standing.settled")
                              : statusName(status)}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.secondary" }}
                >
                  {t("progress.settledWith", {
                    settled: settledWith,
                    count: claim.opponents.length,
                  })}
                </Typography>
              </Box>

              {claim.amounts.map((amount) => {
                const share = (value: number) =>
                  amount.claimed > 0
                    ? Math.min(1, Math.max(0, value / amount.claimed))
                    : 0;
                const settled = share(amount.settled);
                // what is on offer fills no more than what is left open
                const offered = Math.min(share(amount.offered), 1 - settled);
                return (
                  <Box
                    key={amount.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        md: "minmax(190px, 260px) minmax(120px, 1fr) minmax(230px, 320px)",
                      },
                      columnGap: 2.25,
                      rowGap: 0.75,
                      alignItems: "center",
                      px: 1.75,
                      py: 1.25,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        <Named
                          label={t("claimedAmount")}
                          title={amount.title}
                        />
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
                      >
                        {t("progress.claimed", {
                          amount: currency.format(amount.claimed),
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        height: BAR_HEIGHT,
                        borderRadius: 999,
                        overflow: "hidden",
                        bgcolor: "action.selected",
                      }}
                    >
                      <Box
                        title={t("progress.settledTip", {
                          amount: currency.format(amount.settled),
                        })}
                        sx={{
                          width: `${settled * 100}%`,
                          bgcolor: gainFill(theme),
                        }}
                      />
                      <Box
                        title={t("progress.offeredTip", {
                          amount: currency.format(amount.offered),
                        })}
                        sx={{
                          width: `${offered * 100}%`,
                          bgcolor: offeredFill,
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
                      >
                        <Trans
                          i18nKey="case.analytics.settlement.progress.settledOf"
                          values={{
                            amount: currency.format(amount.settled),
                            share: percentage.format(settled),
                          }}
                          components={{ bold }}
                        />
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          columnGap: 1.25,
                          typography: "caption",
                          color: "text.secondary",
                          ...TABULAR_NUMERALS,
                        }}
                      >
                        {amount.parts.length === 0 && t("progress.uncovered")}
                        {amount.parts.map(
                          ({ opponent, accepted, amount: towards }) => (
                            <span key={opponent.id}>
                              {t(
                                accepted
                                  ? "progress.paidBy"
                                  : "progress.onOfferWith",
                                {
                                  name: opponent.name,
                                  amount: currency.format(towards),
                                },
                              )}
                            </span>
                          ),
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SettlementSummary;
